import { type FC } from 'react';

import type { WorkProject } from '../../types';

interface NextCaseSectionProps {
  nextProject?: WorkProject | null;
  onAdvance?: () => void;
}

const getPrimarySource = (project?: WorkProject | null) => {
  if (!project) return null;
  if (project.mediaType === 'video') {
    return project.mediaSources?.[0]?.src ?? project.media ?? null;
  }
  return project.media ?? null;
};

const NextCaseSection: FC<NextCaseSectionProps> = ({ nextProject, onAdvance }) => {
  if (!nextProject || !onAdvance) {
    return null;
  }

  const mediaSrc = getPrimarySource(nextProject);
  const Logo = nextProject?.logo;

  return (
    <section className="w-full px-6 pb-20 pt-6 sm:px-10 lg:px-16">
      <div className="mx-auto max-w-[96rem]">
        <div className="relative overflow-hidden rounded-[32px] bg-transparent">
          <div className="relative aspect-[20/9] w-full overflow-hidden">
            {nextProject?.mediaType === 'video' && mediaSrc ? (
              <video
                autoPlay
                className="size-full object-cover"
                loop
                muted
                playsInline
                src={mediaSrc}
              >
                {nextProject.mediaSources?.map(({ src, type }, index) => (
                  <source key={`${src}-${type ?? 'video'}-${index}`} src={src} type={type} />
                ))}
              </video>
            ) : mediaSrc ? (
              <img
                alt={`${nextProject?.title ?? 'Next case'} preview`}
                className="size-full object-cover"
                decoding="async"
                loading="lazy"
                src={mediaSrc}
              />
            ) : (
              <div className="size-full bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900" />
            )}

            <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-black/90 via-black/45 to-black/10" />

            <div className="absolute inset-0 flex flex-col items-center justify-center gap-8 px-6 text-center sm:px-12">
              {Logo ? (
                <Logo
                  aria-label={`${nextProject?.title ?? 'Next case'} logo`}
                  className="h-16 w-auto text-white/90"
                  role="img"
                />
              ) : (
                <span className="font-konexy text-[clamp(2.75rem,6.2vw,5.5rem)] leading-none lowercase text-white">
                  {nextProject?.title?.toLowerCase() ?? 'next case'}
                </span>
              )}

              <button
                className="group inline-flex items-center gap-3 rounded-full border border-white/30 bg-white/10 px-6 py-3 text-sm font-medium uppercase tracking-[0.3em] text-white transition hover:border-white/60 hover:bg-white/20 disabled:cursor-not-allowed disabled:border-white/20 disabled:bg-white/5 disabled:text-white/40"
                onClick={onAdvance}
                type="button"
              >
                Continue To Next
                <span className="block h-0.5 w-8 translate-x-0 bg-white/60 transition group-hover:w-12 group-hover:bg-white" />
              </button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default NextCaseSection;
